"use client";

import * as React from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useEditableField } from "@/hooks/useEditableField";
import { TruncatedText } from "@/components/ui/primitives/truncated-text";

export interface EditableFieldProps {
  label: string;
  value: string;
  onSave?: (newValue: string) => void;
  onEdit?: () => void;
  editable?: boolean;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  placeholder?: string;
  maxLength?: number;
  type?: "text" | "email";
  isError?: boolean;
  errorMessage?: string;
  onBlur?: (value: string) => void;
  isSaving?: boolean;
}


/**
 * EditableField - Displays a label and value with inline editing capability
 * 
 * Features:
 * - Click edit button or field to enter edit mode
 * - Press Enter to save, Escape to cancel
 * - Auto-focuses and selects input when entering edit mode
 * - Blocks editing when isSaving is true
 * - Only saves if value actually changed
 * - Does NOT perform validation - parent component controls error state
 * - Automatically truncates long text with tooltip showing full content
 * 
 * Layout:
 * - Label on top (body-xs-regular, text-quaternary)
 * - Value below (body-sm-regular, text-primary) or input when editing
 * - Edit button on the right (pencil icon) when editable
 * 
 * Used in profile pages and forms where fields can be edited inline.
 */
function EditableField({
  label,
  value,
  onSave,
  onEdit,
  editable = true,
  className,
  labelClassName,
  valueClassName,
  placeholder,
  maxLength,
  type = "text",
  isError = false,
  errorMessage,
  onBlur,
  isSaving = false,
}: EditableFieldProps) {
  const fieldId = React.useId();
  const inputId = `${fieldId}-input`;
  const errorId = `${fieldId}-error`;

  const {
    isEditing,
    inputValue,
    inputRef,
    canEdit,
    handleStartEdit,
    handleInputChange,
    handleKeyDown,
    handleBlur,
  } = useEditableField({
    value,
    onSave,
    onEdit,
    editable,
    isSaving,
    isError,
    onBlur,
  });

  const handleDisplayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleStartEdit();
    }
  };

  // Display mode (not editing)
  if (!isEditing) {
    const hasValue = Boolean(value);
    const displayValue = value || "-";

    return (
      <div
        className={cn(
          "flex items-end justify-between",
          isError ? "min-h-14" : "h-14",
          className
        )}
        role="group"
        aria-label={`${label} field`}
      >
        <div className="flex flex-[1_0_0] flex-col gap-0.5 h-full min-w-0">
          <label
            htmlFor={inputId}
            className={cn(
              "body-xs-regular text-text-quaternary h-5.25 flex items-center",
              "pointer-events-none",
              labelClassName
            )}
          >
            {label}
          </label>
          <div
            className={cn(
              "flex items-center justify-between rounded-md relative transition-all duration-200 ease-out h-8.25",
              isError
                ? "border border-(--input-border-error) px-[10.5px] py-1.75"
                : "border border-transparent",
              canEdit && "group cursor-text hover:px-[10.5px] group-hover:py-1.75",
              !canEdit && "cursor-default"
            )}
            onClick={canEdit ? handleStartEdit : undefined}
            onKeyDown={canEdit ? handleDisplayKeyDown : undefined}
            tabIndex={canEdit ? 0 : -1}
            role={canEdit ? "button" : undefined}
            aria-label={canEdit ? `Edit ${label.toLowerCase()}` : undefined}
            aria-disabled={!canEdit}
          >
            <div
              className={cn(
                "absolute inset-0 rounded-md transition-colors duration-200 ease-out",
                canEdit && "bg-transparent group-hover:bg-surface-100"
              )}
            />
            <div className="relative flex-1 flex items-center min-h-0 max-w-[200px]">
              <TruncatedText
                text={displayValue}
                className={cn(
                  "body-sm-regular text-text-primary transition-all duration-200 ease-out",
                  !hasValue && "text-text-placeholder",
                  valueClassName
                )}
              />
            </div>
            {editable && (
              <div className="relative flex items-center justify-center shrink-0 size-7.75 ml-2">
                {isSaving ? (
                  <div
                    className="flex items-center justify-center size-7.75"
                    aria-label="Saving..."
                    role="status"
                  >
                    <i className="pi pi-spin pi-spinner text-sm text-text-secondary" />
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartEdit();
                    }}
                    className="shrink-0"
                    aria-label={`Edit ${label.toLowerCase()}`}
                    disabled={!canEdit}
                  >
                    <i className="pi pi-pencil text-sm text-text-secondary" />
                  </Button>
                )}
              </div>
            )}
          </div>
          {isError && errorMessage && (
            <p
              id={errorId}
              className="body-xs-regular text-(--input-helper-error) mt-1"
              role="alert"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      className={cn(
        "flex flex-col gap-0.5 items-start w-full transition-all duration-200 ease-out",
        isError ? "min-h-14" : "h-14",
        className
      )}
      role="group"
      aria-label={`Editing ${label.toLowerCase()}`}
    >
      <div className="flex flex-col gap-1 items-start w-full">
        <label
          htmlFor={inputId}
          className={cn(
            "body-xs-regular text-text-quaternary h-5.25 flex items-center",
            labelClassName
          )}
        >
          {label}
        </label>
        <div className="w-full">
          <input
            id={inputId}
            ref={inputRef}
            type={type}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || value || ""}
            maxLength={maxLength}
            disabled={isSaving}
            className={cn(
              "w-full body-sm-regular text-text-primary bg-bg-primary border rounded-sm px-[10.5px] py-1.75 shadow-(--input-shadow) focus:outline-none h-8.25 transition-all duration-200 ease-out",
              isError
                ? "border-(--input-border-error) focus:border-(--input-border-error)"
                : "border-(--input-border-focus) focus:border-(--input-border-focus)",
              "placeholder:text-text-placeholder",
              isSaving && "opacity-50 cursor-not-allowed",
              valueClassName
            )}
            aria-invalid={isError}
            aria-describedby={isError && errorMessage ? errorId : undefined}
          />
          {isError && errorMessage && (
            <p
              id={errorId}
              className="body-xs-regular text-(--input-helper-error) mt-1"
              role="alert"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

EditableField.displayName = "EditableField";

export { EditableField };
